import { useState, type FormEvent } from 'react';
import type { TenantInvoice, MarkInvoicePaidDto } from '@yawi/types';
import { formatCurrency } from '@yawi/infrastructure';
import { Button, Input, Textarea, Modal } from '@/core/ui/components';

interface MarkInvoicePaidModalProps {
  invoice: TenantInvoice;
  onClose: () => void;
  onConfirm: (invoiceId: string, dto: MarkInvoicePaidDto) => Promise<void>;
}

export function MarkInvoicePaidModal({ invoice, onClose, onConfirm }: MarkInvoicePaidModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onConfirm(invoice.id, {
        stripePaymentIntentId: stripePaymentIntentId || undefined,
        notes: notes || undefined,
      });
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Marcar como Pagada"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            Confirmar Pago
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <div className="flex justify-between mb-2">
            <span className="text-theme-secondary text-sm">Factura</span>
            <span className="text-theme font-medium">{invoice.invoiceNumber}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-theme-secondary text-sm">Periodo</span>
            <span className="text-theme">{invoice.period}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-theme-secondary text-sm">Total</span>
            <span className="text-theme font-bold">{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
          </div>
        </div>

        <Input
          label="Stripe Payment Intent ID (opcional)"
          value={stripePaymentIntentId}
          onChange={(e) => setStripePaymentIntentId(e.target.value)}
          placeholder="pi_..."
        />

        <Textarea
          label="Notas"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Pago recibido via transferencia..."
        />
      </form>
    </Modal>
  );
}
